import { useEffect, useRef, RefObject } from 'react';
import { eventBus, EventNames } from '../../../../utils/eventBus';
import type { FormattedTextInputHandle } from '../../../FormattedTextInput';

/**
 * Hook for listening to suggested question events
 *
 * Uses refs for callbacks to avoid re-subscribing to EventBus
 * on every keystroke (sendMessage changes with inputMessage).
 */

interface UseSuggestedQuestionsProps {
  sendMessage: (message: string, options?: { studio_id?: string }) => void;
  setInputMessage: (message: string) => void;
  formattedInputRef: RefObject<FormattedTextInputHandle | null>;
}

export function useSuggestedQuestions({
  sendMessage,
  setInputMessage,
  formattedInputRef,
}: UseSuggestedQuestionsProps) {
  // Use refs to always access the latest callbacks without re-subscribing
  const sendMessageRef = useRef(sendMessage);
  const setInputMessageRef = useRef(setInputMessage);

  sendMessageRef.current = sendMessage;
  setInputMessageRef.current = setInputMessage;

  useEffect(() => {
    const handleSuggestedQuestion = (data: {
      question: string;
      autoSend?: boolean;
      studio_id?: string;
    }) => {
      if (data?.question) {
        if (data.autoSend) {
          const options = data.studio_id ? { studio_id: data.studio_id } : undefined;
          sendMessageRef.current(data.question, options);
        } else {
          setInputMessageRef.current(data.question);
          formattedInputRef.current?.focus();
        }
      }
    };

    eventBus.on(EventNames.SUGGESTED_QUESTION_CLICKED, handleSuggestedQuestion);
    return () => {
      eventBus.off(EventNames.SUGGESTED_QUESTION_CLICKED, handleSuggestedQuestion);
    };
  }, [formattedInputRef]);
}
